import React from 'react';
// mui components
import { Avatar, Box, LinearProgress, Paper, Typography } from '@mui/material';
// hooks
import { IArtistProfile } from 'hooks/swrRequests/useArtistProfiles';
// config
import { getProfileProgress } from '../steps';
import { useTranslation } from 'react-i18next';

const GREEN = '#1F9D6B';

interface OwnProps {
  profile: IArtistProfile;
  /** Artist name shown in the copy when the user manages several profiles. */
  artistName?: string | null;
}

const OnboardingBanner: React.FC<OwnProps> = ({ profile, artistName }) => {
  const { t } = useTranslation();
  const { done, total } = getProfileProgress(profile);
  const completed = profile.completed;
  const pct = Math.round((done / total) * 100);

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2.2,
        p: '18px 22px',
        borderRadius: 3,
        mb: 3.2,
        bgcolor: completed ? '#EAF7F1' : '#FFFCEB',
        border: '1px solid',
        borderColor: completed ? '#BFE7D5' : '#F2E9A8',
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2.5,
          bgcolor: completed ? GREEN : '#FEDC00',
          color: completed ? '#fff' : '#141414',
          fontWeight: 800,
          fontSize: 18,
        }}
      >
        {completed ? '✓' : '!'}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
          {completed
            ? artistName
              ? `${artistName} — ${t('profile complete')}`
              : t('Profile complete')
            : artistName
            ? `${t('Finish setting up')} ${artistName}`
            : t('Finish setting up your account')}
        </Typography>
        <Typography sx={{ fontSize: 16, color: '#6B6B6B' }}>
          {completed
            ? t('Everything is set up. The rest of SongFund is now unlocked.')
            : t('Complete the steps below to unlock Deals, Perks and payouts.')}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            mt: 1.1,
            height: 7,
            borderRadius: 99,
            bgcolor: completed ? '#CDEAD9' : '#EFE7C8',
            '& .MuiLinearProgress-bar': {
              bgcolor: completed ? GREEN : '#141414',
              borderRadius: 99,
            },
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: 16,
          color: '#6B6B6B',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {done} / {total}
      </Typography>
    </Paper>
  );
};

export default OnboardingBanner;
